<script setup>
import { onMounted, onUnmounted, watch } from 'vue'
import { useUserStore } from '@/stores/user'

const userStore = useUserStore()
let refreshTimer = null
let pwdSecurityTimer = null

onMounted(() => {
  // Vue 应用已成功挂载，隐藏 index.html 中的加载动画
  const loading = document.getElementById('app-loading')
  if (loading) {
    loading.style.display = 'none'
  }

  // 图片全屏预览：鼠标任意点击关闭
  document.addEventListener('click', (e) => {
    const viewer = document.querySelector('.el-image-viewer')
    if (!viewer) return
    const mask = viewer.querySelector('.el-image-viewer__mask')
    const canvas = viewer.querySelector('.el-image-viewer__canvas')
    if ((mask && mask.contains(e.target)) || (canvas && canvas.contains(e.target))) {
      // 确认点击的是图片本身以外的区域（点击遮罩或空白处关闭）
      if (e.target.tagName === 'IMG') return
      const closeBtn = viewer.querySelector('.el-image-viewer__close')
      if (closeBtn) closeBtn.click()
    }
  }, true)

  // 恢复登录状态后，若已登录则立即刷新 token 并检查密码安全
  userStore.loadFromStorage()
  if (userStore.isLoggedIn) {
    userStore.refreshToken()
    userStore.checkPwdSecurity()
  }

  // 每 1 小时自动刷新 token
  refreshTimer = setInterval(() => {
    if (userStore.isLoggedIn) {
      userStore.refreshToken()
    }
  }, 3600000) // 1小时 = 3600000ms

  // 每 10 分钟检查一次密码安全
  pwdSecurityTimer = setInterval(() => {
    if (userStore.isLoggedIn) {
      userStore.checkPwdSecurity()
    }
  }, 600000) // 10分钟 = 600000ms
})

// 监听登录状态变化：当从未登录变为已登录时，立即检查密码安全
watch(
  () => userStore.isLoggedIn,
  (newVal, oldVal) => {
    if (newVal && !oldVal) {
      userStore.checkPwdSecurity()
    }
  }
)

onUnmounted(() => {
  if (refreshTimer) {
    clearInterval(refreshTimer)
    refreshTimer = null
  }
  if (pwdSecurityTimer) {
    clearInterval(pwdSecurityTimer)
    pwdSecurityTimer = null
  }
})
</script>

<template>
  <router-view />
</template>

<style>
/* 全局样式重置 */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

:root {
  /* 律师事务所主题色 */
  --law-primary: #1a365d; /* 深蓝/藏青色 - 主色 */
  --law-secondary: #2c5282; /* 中深蓝 - 辅助色 */
  --law-accent: #b7791f; /* 金色 - 点缀色 */
  --law-accent-light: #d69e2e; /* 浅金色 */
  --law-bg: #f7fafc; /* 浅灰背景 */
  --law-text: #2d3748; /* 深灰文字 */
  --law-text-light: #718096; /* 浅灰文字 */
  --law-border: #e2e8f0; /* 边框色 */
  --law-white: #ffffff;
  --law-success: #38a169;
  --law-warning: #d69e2e;
  --law-danger: #e53e3e;
}

body {
  font-family: 'Segoe UI', 'Microsoft YaHei', '微软雅黑', Arial, sans-serif;
  min-width: 1024px;
  height: 100%;
  background-color: var(--law-bg);
  color: var(--law-text);
  line-height: 1.6;
}

ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

textarea {
  resize: none;
}

/* 链接样式 */
.link {
  color: var(--law-secondary);
  font-size: 14px;
  cursor: pointer;
  text-decoration: none;
  transition: color 0.3s ease;
}
.link:hover {
  color: var(--law-accent);
  text-decoration: underline;
}

/* 必填提示 */
.require:before {
  content: '*';
  color: var(--law-danger);
}

/* 加载动画样式 */
.app-loading {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, var(--law-primary) 0%, var(--law-secondary) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.loading-container {
  text-align: center;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid var(--law-accent);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

.loading-text {
  color: var(--law-white);
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 1px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* 图片全屏预览：点击任意处关闭 */
.el-image-viewer__mask {
  cursor: pointer;
}
.el-image-viewer__canvas {
  cursor: pointer;
}
.el-image-viewer__canvas img {
  cursor: default;
}

/* Logo 大图预览弹窗：无蒙层，居中正方形 + 关闭按钮 */
.logo-preview-dialog .el-dialog {
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.18);
  border-radius: 10px;
  overflow: hidden;
  width: 340px !important;
}
.logo-preview-dialog .el-dialog__header {
  display: none;
}
.logo-preview-dialog .el-dialog__body {
  padding: 0;
}
.logo-preview-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 20px 16px;
}
.logo-preview-full {
  width: 300px;
  height: 300px;
  object-fit: contain;
  border-radius: 6px;
}
.logo-preview-close {
  margin-top: 14px;
}
</style>
